import React, {useEffect, useState} from 'react'
import {observer} from 'mobx-react-lite'
import {useBasketItem} from '../../store/hooks/useBasketItem'
import {useBasketStore, useContentStore, useCustomerStore, useOrderStore} from '../../store/hooks/useStore'
import {usePromotion} from '../../store/hooks/usePromotion'
import {BaseButton, Box, BoxProps, Button, Flex, Stack, Text} from '../../vanilla'
import {Link} from '../link'
import {MultiBuyPromoOfferMessage, NearPromo} from '../product-detail/product-promo'
import {Price} from '../price'
import {TrashIcon} from '../icons'
import LoadingOverlay from '../loading-overlay'
import {
  cartItemActions,
  cartItemDetails,
  cartItemDetailsName,
  cartItemImage,
  cartItemPrice,
  cartItemQuantity,
  orderEditModeChanged,
} from './styles.css'
import classNames from 'classnames'
import {useItemPrice} from '../algolia/util/useItemPrice'
import {useCategory} from '../../store/hooks/useCategory'
import {usePreviouslyRemovedItem} from '../../store/hooks/usePreviouslyRemovedItem'
import UpdateCartItemQuantity from '../update-cart-quantity'
import {useRemoveFromCartTracking} from '../../analytics/click-event-tracking'
import {CART_LOCATION} from '../../analytics/utils'
import {useSlotExpiration} from '../../store/hooks/useSlotExpiration'
import {Hit} from '../../store/ProductStore'
import {useAlcoholRestrictions} from '../../hooks/use-alcohol-restrictions'
import {formatProductName} from '../../utils/utils'
import {ProductImage} from '../product-detail/product-image'
import { ProductItem } from '../../store/BasketStore'
import { PartialProductItem } from '../../types/store/basket'
import { createRestrictedMessage } from '../algolia/util/utils'

interface CartItemProps extends BoxProps<'div'> {
  id: string
  className?: string
  inDrawer?: boolean
  isUnavailable?: boolean
}

const useProductCategoryRoute = (id: string, isUnavailable: boolean | undefined) => {
  const previouslyRemoveditem = usePreviouslyRemovedItem(id)
  const basketItem = useBasketItem(id)
  const {product} = isUnavailable ? previouslyRemoveditem : basketItem
  const category = useCategory(product?.categoryPageId?.[0])
  const {routeMap} = useContentStore()

  const [categoryRoute] =
    Object.entries(routeMap).find(([route, dataStr]) => {
      const [_, __, entityId] = dataStr.split('|')

      return entityId === category?.id
    }) || []

  return categoryRoute
}

const CartItemContainer = ({id, className, inDrawer, isUnavailable, ...props}: CartItemProps) => {
  const {editMode, orderEditProductItemsDelta} = useOrderStore()
  const basketStore = useBasketStore()
  const [clickedQuantity, setClickedQuantity] = useState(0)
  const sendRemoveFromCartData = useRemoveFromCartTracking()
  const {isReservationExpired, handleEditReservationExpired} = useSlotExpiration()

  const previouslyRemovedItem = usePreviouslyRemovedItem(id)
  const curBasketItem = useBasketItem(id)
  const {item: basketItem, product} = isUnavailable
    ? previouslyRemovedItem
    : curBasketItem

  const {checkIfProductAlcohol, isCurrentSlotAlcoholRestricted} = useAlcoholRestrictions()
  const isAlcohol = product ? checkIfProductAlcohol(product as unknown as Hit) : false
  const {getApplicablePromotions} = useCustomerStore()
  const {multiBuyPromo, nearPromo, allPromos, bundle} = getApplicablePromotions(product?.productPromotions)

  // Restricted product classes
  const restrictedProductInfo = basketStore.getRestrictedProductInfo(product?.productClasses, product?.id)
  const disabledProductBecauseOfRestriction = (isAlcohol && isCurrentSlotAlcoholRestricted)
  const isAvailableProduct = !isUnavailable && !disabledProductBecauseOfRestriction

  useEffect(() => {
    setClickedQuantity(basketItem?.quantity)
  }, [basketStore.count])

  const promotion = usePromotion(multiBuyPromo?.ID)

  const {displayPrice, promotionPrice} = useItemPrice(product)
  const categoryRoute = useProductCategoryRoute(id, isUnavailable)

  const productLink = `/p/${formatProductName(product?.name)}/${product?.id}.html`

  // @todo format the algolia data to be consistent across PLP and PDP
  const img = product?.image_groups?.[0]?.images?.[0] || product?.imageGroups?.[0]?.images?.[0]

  if (!product) return null

  const hasAnyPromo = (allPromos !== null && allPromos.length > 0) || bundle

  const orderEditModeProductChanged =
    editMode && !inDrawer && orderEditProductItemsDelta[product.id]


  const getCartLocation = () => {
    if (location.pathname === '/basket') {
      return CART_LOCATION.BASKET
    }
    if (location.pathname === '/checkout/summary') {
      return CART_LOCATION.CHECKOUT
    }
    return CART_LOCATION.MINIBASKET
  }
  const locationOfCart = getCartLocation()
  const isMiniBasket = locationOfCart === 'Mini Basket'

  const removeFromBasket = () => {
    if (editMode && isReservationExpired()) {
      // If in edit mode and slot has expired send to order history and don't add to basket
      handleEditReservationExpired()
    } else if (isUnavailable) {
      basketStore.removePreviouslyRemovedItem(id)
    } else {
      basketStore.addProductToUpdate({product: product as unknown as PartialProductItem, quantity: 0})
      sendRemoveFromCartData(product, locationOfCart)
    }
  }

  return (
    <Flex
      position="relative"
      padding="16px"
      justify="space-between"
      flexWrap="wrap"
      alignItems="center"
      borderBottom="1px"
      borderColor="gray200"
      paddingBottom={['0px', '16px']}
      className={classNames(className, {[orderEditModeChanged]: orderEditModeProductChanged})}
      style={{
        backgroundColor: isAvailableProduct ? '#fff' : '#fbf7f8',
      }}
      {...props}
    >
      <LoadingOverlay isLoading={clickedQuantity !== basketItem?.quantity} />
      <Box
        as={Link}
        href={productLink}
        ratio="1"
        width="full"
        flexShrink="0"
        position="relative"
        className={cartItemImage}
        marginBottom={!hasAnyPromo ? '16px' : '0px'}
        paddingRight={isMiniBasket ? '16px' : '0px'}
      >
        <ProductImage
          image={img}
          imageAlt={img?.alt}
          isThumbnail={true}
          inDrawer={inDrawer}
          minHeight={['auto', 'full']}
          style={{objectFit: 'contain'}}
        />
      </Box>
      <Box className={cartItemDetails} alignSelf="flex-start">
        <Stack spacing="0px" paddingLeft={[!isMiniBasket ? '16px' : '0px', '0px']}>
          <Text
            as={Link}
            color={{default: 'gray800', hover: 'gray700'}}
            to={productLink}
            variant="text3"
            lineHeight="short"
            className={cartItemDetailsName}
            data-test-selector={inDrawer ? 'cart-item-name' : 'product-list-item-name'}

          >
            {product.name}
          </Text>
          {restrictedProductInfo?.cannotAddToBasket && product?.productClasses?.length > 0 && (
            <Text color='accent0'>{createRestrictedMessage(restrictedProductInfo?.restrictedProductLimit)}</Text>
          )}
          {disabledProductBecauseOfRestriction && (
              <Text color='accent0' lineHeight={'normal'} marginBottom='16px'>
                Sorry, we can't deliver alcohol between 10pm and 10am.<br/>Remove this item from your basket or choose a different delivery slot.
              </Text>
        )}
          {promotion?.calloutMsg && !inDrawer && (
            <Text variant="text3" color="accent0" lineHeight="short">
              {promotion.calloutMsg}
            </Text>
          )}
        </Stack>
        {!inDrawer && isAvailableProduct ? (
          <>
            <Flex flexDirection="column" alignItems="flex-start" marginTop="16px" gap="8px" paddingLeft={['16px', '0px']}>
              {(nearPromo || bundle) && (
                <NearPromo
                  position="static"
                  promotion={nearPromo}
                  hitId={product.id}
                  bundle={bundle}
                />
              )}
              {nearPromo && (
                <MultiBuyPromoOfferMessage
                  promotion={nearPromo}
                  productId={product.id}
                  leftAlign
                />
              )}
            </Flex>
            {orderEditModeProductChanged && (
              <Box marginTop="4px">
                <Text variant="text3" lineHeight="short" color="promotionColorDark" data-test-selector="recently-added-removed">
                  {orderEditModeProductChanged.deltaQty
                    ? `${Math.abs(orderEditModeProductChanged.deltaQty)} ${
                        orderEditModeProductChanged.deltaQty < 1 ? 'Removed' : 'Added'
                      }`
                    : orderEditModeProductChanged.recentlyAdded
                    ? 'Recently Added'
                    : null}
                </Text>
              </Box>
            )}
          </>
        ) : null}
        {inDrawer && hasAnyPromo && isAvailableProduct ? (
          <Flex flexDirection="column" marginY="8px" gap="4px" width="full">
            {bundle ? (
              <NearPromo
                position="static"
                bundle={bundle}
                cartDrawer={true}
                hitId={product.id}
              />
            ): null}
            {allPromos.map((promo) => (
              <Flex key={promo.ID} flexDirection="column" width="full" gap="4px">
                <NearPromo
                  position="static"
                  promotion={promo}
                  cartDrawer={true}
                  hitId={product.id}
                />
                <MultiBuyPromoOfferMessage
                  promotion={promo}
                  productId={product.id}
                  leftAlign
                />
              </Flex>
            ))}
          </Flex>
        ) : null}
      </Box>
      {!disabledProductBecauseOfRestriction && isAvailableProduct && (
        <Box className={cartItemQuantity}>
          <UpdateCartItemQuantity
            product={product as unknown as ProductItem}
            quantity={clickedQuantity || 0}
            setQuantity={setClickedQuantity}
            maxQuantity={restrictedProductInfo?.maxQuantityAllowed}
            cartLocation={locationOfCart}
            size="sm"
            variant="outlineDark"
            paddingRight={['0px', '16px']}
            inDrawer={inDrawer}
          />
        </Box>
      )}
      {!disabledProductBecauseOfRestriction && !isAvailableProduct && (
        <Box>
          <Text lineHeight="none" color="accent0">
            Product <br /> unavailable &nbsp;
          </Text>
        </Box>
      )}
      {isAvailableProduct || disabledProductBecauseOfRestriction ? (
        <Box className={cartItemPrice} paddingLeft={disabledProductBecauseOfRestriction ? "52px" : "0px"} marginLeft={'auto'} marginRight={'auto'}>
            <Price
              promotionPrice={promotionPrice ?? null}
              customPriceAmount={!promotionPrice && displayPrice ? displayPrice * basketItem.quantity : null}
              price={displayPrice}
              quantity={basketItem?.quantity}
              priceInfo={product.priceInfo}
              isBasket={true}
              dataTestSelector={inDrawer ? 'cart-list-item-price' : undefined}
            />
        </Box>
      ) : (
        <Box>
          <BaseButton variant="outlineLight" fontSize="xs" as={Link} to={categoryRoute}>
            <Text>Find Replacement</Text>
          </BaseButton>
        </Box>
      )}
      <Box className={cartItemActions}>
        <Button
          onClick={() => {
            setClickedQuantity(0)
            removeFromBasket()
          }}
          variant="unstyled"
          paddingX="0px"
          iconLeft={<TrashIcon color="gray800" style={{height: 22, width: 20}} />}
          style={{height: 'auto'}}
          data-test-selector="remove-icon"
        />
      </Box>
    </Flex>
  )
}

export const CartItem = observer(CartItemContainer)